import React from 'react'
import { List, useTable } from '@refinedev/antd'
import { useList, useGetIdentity } from '@refinedev/core'
import { Table } from 'antd'
import { brand } from '../../theme/brand'
import { renderStatus } from '../../theme/columns'
import { ListPage, Pill, TableSurface, type Stat } from '../../theme/listkit'
import { UsersRound, ShieldCheck, ShieldAlert, Activity } from '../../components/icons'
import { PromoteAdmin } from './PromoteAdmin'
import { RevokeAdminButton } from './RevokeAdminButton'

interface AdminUser {
  user_id: string
  email: string | null
  name: string | null
  class_year: string | null
  role: string | null
  created_at: string
  last_sign_in_at: string | null
  email_confirmed_at: string | null
  banned_until: string | null
  is_banned: boolean
}

const fmt = (v: string | null) =>
  v ? (
    new Date(v).toLocaleString()
  ) : (
    <span style={{ color: brand.sub }}>—</span>
  )

/**
 * Sign-ups — read-only roster from the admin_users view (auth.users + profiles,
 * admin-gated). Shows who joined, confirmation + last-seen, and ban status.
 */
export function AdminUsersList() {
  const { tableProps } = useTable<AdminUser>({
    resource: 'admin_users',
    syncWithLocation: true,
    meta: { idColumnName: 'user_id' },
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
  })

  // The signed-in admin's id — used to never offer "Revoke admin" on your OWN
  // row (no self-lockout; since you can't revoke yourself, at least one admin
  // always remains).
  const { data: identity } = useGetIdentity<{ id?: string }>()
  const currentUserId = identity?.id

  // Summary-deck counts come from BOUNDED pageSize:1 queries instead of pulling
  // the whole roster and reducing it on the main thread. PostgREST returns the
  // exact `total` per filter without shipping rows, and each tiny query runs in
  // parallel. liveMode 'off' is REQUIRED on each: useTable already opens the
  // realtime channel for `admin_users`, and a second live subscription to the
  // same resource throws ("cannot add postgres_changes …").
  const weekAgo = React.useMemo(
    () => new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    [],
  )
  const common = { resource: 'admin_users', liveMode: 'off' as const, meta: { idColumnName: 'user_id' } }
  const { result: totalRes } = useList<AdminUser>({ ...common, pagination: { pageSize: 1 } })
  const { result: confirmedRes } = useList<AdminUser>({
    ...common,
    pagination: { pageSize: 1 },
    filters: [{ field: 'email_confirmed_at', operator: 'nnull', value: true }],
  })
  const { result: bannedRes } = useList<AdminUser>({
    ...common,
    pagination: { pageSize: 1 },
    filters: [{ field: 'is_banned', operator: 'eq', value: true }],
  })
  const { result: newWeekRes } = useList<AdminUser>({
    ...common,
    pagination: { pageSize: 1 },
    filters: [{ field: 'created_at', operator: 'gte', value: weekAgo }],
  })

  const totalCount = totalRes?.total ?? 0
  const confirmed = confirmedRes?.total ?? 0
  const banned = bannedRes?.total ?? 0
  const newThisWeek = newWeekRes?.total ?? 0

  const stats: Stat[] = [
    { label: 'Total Sign-ups', value: totalCount.toLocaleString(), icon: UsersRound, tone: 'jaguar' },
    {
      label: 'Confirmed',
      value: confirmed.toLocaleString(),
      hint: totalCount ? `${Math.round((confirmed / totalCount) * 100)}%` : undefined,
      icon: ShieldCheck,
      tone: 'green',
    },
    { label: 'Bans', value: banned.toLocaleString(), icon: ShieldAlert, tone: banned > 0 ? 'red' : 'neutral' },
    { label: 'New This Week', value: newThisWeek.toLocaleString(), hint: 'last 7 days', icon: Activity, tone: 'gold' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Members · Accounts & Admins',
        description:
          'Everyone who has created an account, drawn from auth and their profile. Confirm onboarding, watch last-seen activity, and act on ban status — the front door to the alumni community.',
      }}
      stats={stats}
    >
      <PromoteAdmin />
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="user_id" scroll={{ x: true }}>
            <Table.Column
              dataIndex="email"
              title="Email"
              render={(v: string | null) =>
                v ? (
                  <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>{v}</span>
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )
              }
            />
            <Table.Column
              dataIndex="name"
              title="Name"
              render={(v: string | null) =>
                v ? (
                  <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>{v}</span>
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )
              }
            />
            <Table.Column dataIndex="class_year" title="Class" render={renderStatus} />
            <Table.Column
              dataIndex="role"
              title="Role"
              render={(v: string | null) =>
                v === 'admin' ? <Pill tone="gold">admin</Pill> : <Pill tone="neutral">{v ?? 'member'}</Pill>
              }
            />
            <Table.Column
              dataIndex="email_confirmed_at"
              title="Confirmed"
              render={(v: string | null) =>
                v ? <Pill tone="green">Yes</Pill> : <Pill tone="neutral">No</Pill>
              }
            />
            <Table.Column dataIndex="created_at" title="Signed up" render={fmt} />
            <Table.Column dataIndex="last_sign_in_at" title="Last seen" render={fmt} />
            <Table.Column
              dataIndex="is_banned"
              title="Status"
              render={(isBanned: boolean) =>
                isBanned ? <Pill tone="red">Banned</Pill> : <Pill tone="green">Active</Pill>
              }
            />
            <Table.Column
              title="Manage"
              dataIndex="manage"
              render={(_: unknown, record: AdminUser) => {
                if (record.role !== 'admin') return <span style={{ color: brand.sub }}>—</span>
                if (record.user_id === currentUserId) return <Pill tone="gold">You</Pill>
                return <RevokeAdminButton userId={record.user_id} name={record.name} />
              }}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
